import React from 'react';
import { View, ScrollView, StyleSheet, Text, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../../../styles/theme';
import { PantryLocation } from '../../../utils/pantryLocation';
import { ZONE_META } from '../zoneMeta';
import { AddFoodContent } from '../AddFoodContent';

interface Props {
  zone: PantryLocation;
  onClose: () => void;
  onAdd: (food: { name: string; category: string }) => void;
  onPhoto: () => void;
}

export function AddPanel({ zone, onClose, onAdd, onPhoto }: Props) {
  const { t } = useTranslation();
  const zoneMeta = ZONE_META[zone];

  return (
    <View style={styles.container}>
      <PanelHeader
        title={t('pantry.add.toZone', { zone: t(zoneMeta.labelKey).toLowerCase() })}
        subtitle={t('pantry.add.addPanelSub')}
        onClose={onClose}
      />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        scrollEnabled={true}
      >
        <AddFoodContent
          zone={zone}
          onAdd={onAdd}
          onPhoto={onPhoto}
          showPhotoButton={true}
          showSearchField={true}
          showRecentsLabel={true}
        />
      </ScrollView>
    </View>
  );
}

function PanelHeader({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
}) {
  return (
    <View style={styles.header}>
      <View style={styles.headerContent}>
        <Text style={styles.headerTitle}>{title}</Text>
        {!!subtitle && <Text style={styles.headerSubtitle}>{subtitle}</Text>}
      </View>
      <Pressable style={styles.closeButton} onPress={onClose}>
        <Feather name="x" size={18} color={Paprika.textSecondary} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    paddingVertical: 22,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.border,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
    color: Paprika.textPrimary,
    letterSpacing: -0.015,
    lineHeight: 28,
    marginBottom: 6,
  },
  headerSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    color: Paprika.textMuted,
  },
  closeButton: {
    width: 34,
    height: 34,
    borderRadius: 999,
    backgroundColor: Paprika.surfaceHighest,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingVertical: 18,
    paddingHorizontal: 24,
  },
});
